import { applySoftSepSpatial } from "../../../sim";
import { U, roleStats } from "./accessors";
import type { InfiniteWarState, IwTeam, IwUnit } from "./types";

export type SwarmApproachKind = "trickle" | "march" | "chase";

type CrowdKnobs = {
  engageRange: number;
  maxChaseDistance: number;
  stickRange: number;
  rearwardLeash: number;
  marchSpeedMul: number;
  trickleSpeedMul: number;
  packSplitRadius: number;
  packSplitMax: number;
  sepRadiusMul: number;
};

function crowdKnobs(): CrowdKnobs {
  const c = U.crowd as typeof U.crowd & Partial<CrowdKnobs>;
  return {
    engageRange: c.engageRange ?? 190,
    maxChaseDistance: c.maxChaseDistance ?? 220,
    stickRange: c.stickRange ?? 275,
    rearwardLeash: c.rearwardLeash ?? 56,
    marchSpeedMul: c.marchSpeedMul ?? 0.8,
    trickleSpeedMul: c.trickleSpeedMul ?? 0.32,
    packSplitRadius: c.packSplitRadius ?? 56,
    packSplitMax: c.packSplitMax ?? 5,
    sepRadiusMul: c.sepRadiusMul ?? 1.18,
  };
}

/** March axis on Y: south pushes north (−Y), north pushes south (+Y). */
export function marchForwardY(team: IwTeam): number {
  return team === "south" ? -1 : 1;
}

/** True when foe sits behind the unit past `rearwardLeash` along the march axis. */
export function isRearwardOf(
  unit: IwUnit,
  foe: IwUnit,
  rearwardLeash = crowdKnobs().rearwardLeash,
): boolean {
  const along = (foe.y - unit.y) * marchForwardY(unit.team);
  return along < -rearwardLeash;
}

export function steerToward(unit: IwUnit, tx: number, ty: number, speed: number): void {
  const dx = tx - unit.x;
  const dy = ty - unit.y;
  const distance = Math.hypot(dx, dy) || 1;
  unit.facingX = dx / distance;
  unit.facingY = dy / distance;
  unit.intentVx = unit.facingX * speed;
  unit.intentVy = unit.facingY * speed;
}

export function clearIntent(unit: IwUnit): void {
  unit.intentVx = 0;
  unit.intentVy = 0;
}

/** Unbounded nearest foe — for diagnostics / empty-field checks. Not for combat pursue. */
export function nearestEnemy(units: readonly IwUnit[], unit: IwUnit): IwUnit | undefined {
  let closest: IwUnit | undefined;
  let best = Infinity;
  for (const other of units) {
    if (other.phase !== "alive" || other.team === unit.team) continue;
    const d = (other.x - unit.x) ** 2 + (other.y - unit.y) ** 2;
    if (d < best) {
      best = d;
      closest = other;
    }
  }
  return closest;
}

/** Nearest foe within `maxDist` (leash / acquire band). */
export function nearestEnemyWithin(
  units: readonly IwUnit[],
  unit: IwUnit,
  maxDist: number,
): IwUnit | undefined {
  const max2 = maxDist * maxDist;
  let closest: IwUnit | undefined;
  let best = Infinity;
  for (const other of units) {
    if (other.phase !== "alive" || other.team === unit.team) continue;
    const d = (other.x - unit.x) ** 2 + (other.y - unit.y) ** 2;
    if (d > max2 || d >= best) continue;
    best = d;
    closest = other;
  }
  return closest;
}

/**
 * Forward-biased sticky engage pick for combat.
 * Priority: keep sticky engage → acquire forward/local threat inside chase band
 * → else undefined (caller presses enemy demigod). Never unbounded map-wide nearest
 * (rear spawn must not yank a front press back). Soft≪AA unused.
 */
export function pickEngageTarget(
  units: readonly IwUnit[],
  unit: IwUnit,
  knobs: CrowdKnobs = crowdKnobs(),
): IwUnit | undefined {
  const fwdY = marchForwardY(unit.team);
  const stick2 = knobs.stickRange * knobs.stickRange;
  const chase2 = knobs.maxChaseDistance * knobs.maxChaseDistance;
  const meleeKeep = knobs.engageRange * 0.55;

  const findById = (id: number | null | undefined): IwUnit | undefined => {
    if (id == null) return undefined;
    return units.find(
      (other) => other.id === id && other.phase === "alive" && other.team !== unit.team,
    );
  };

  const sticky = findById(unit.engageTargetId);
  if (sticky) {
    const d = Math.hypot(sticky.x - unit.x, sticky.y - unit.y);
    // Hold sticky while in stick range and not deep rear, or while already in melee.
    if (d <= meleeKeep || (d * d <= stick2 && !isRearwardOf(unit, sticky, knobs.rearwardLeash))) {
      return sticky;
    }
  }

  let best: IwUnit | undefined;
  let bestScore = -Infinity;
  for (const other of units) {
    if (other.phase !== "alive" || other.team === unit.team) continue;
    const dx = other.x - unit.x;
    const dy = other.y - unit.y;
    const d2 = dx * dx + dy * dy;
    if (d2 > chase2) continue;
    if (isRearwardOf(unit, other, knobs.rearwardLeash)) continue;
    const along = dy * fwdY;
    // Prefer forward threats; break ties by proximity.
    const score = along * 0.45 - Math.sqrt(d2);
    if (score <= bestScore) continue;
    bestScore = score;
    best = other;
  }
  return best;
}

export function nearestAllyNeedHeal(
  units: readonly IwUnit[],
  unit: IwUnit,
  maxDist = Infinity,
): IwUnit | undefined {
  const max2 = maxDist * maxDist;
  let closest: IwUnit | undefined;
  let best = Infinity;
  for (const other of units) {
    if (other.phase !== "alive" || other.team !== unit.team || other.id === unit.id) {
      continue;
    }
    if (other.hp >= other.maxHp * 0.92) continue;
    const d = (other.x - unit.x) ** 2 + (other.y - unit.y) ** 2;
    if (d > max2 || d >= best) continue;
    best = d;
    closest = other;
  }
  return closest;
}

/**
 * Among allies in `packSplitRadius`, only the closest `packSplitMax` to `target`
 * may march/chase — rear slots trickle (slow follow) so depth/clumps form
 * without permanent freezes.
 * Tie-break: lower unit id.
 */
export function packAdvanceSlotOk(
  units: readonly IwUnit[],
  unit: IwUnit,
  targetX: number,
  targetY: number,
  packSplitRadius = crowdKnobs().packSplitRadius,
  packSplitMax = crowdKnobs().packSplitMax,
): boolean {
  const r2 = packSplitRadius * packSplitRadius;
  const locals: { id: number; d: number }[] = [];
  for (const ally of units) {
    if (ally.phase !== "alive" || ally.team !== unit.team) continue;
    const toAlly = (ally.x - unit.x) ** 2 + (ally.y - unit.y) ** 2;
    if (toAlly > r2) continue;
    locals.push({
      id: ally.id,
      d: Math.hypot(targetX - ally.x, targetY - ally.y),
    });
  }
  if (locals.length <= packSplitMax) return true;
  locals.sort((a, b) => a.d - b.d || a.id - b.id);
  const rank = locals.findIndex((entry) => entry.id === unit.id);
  return rank >= 0 && rank < packSplitMax;
}

/**
 * Decide trickle / slow march / full chase for a pursuit point.
 * - Pack rear slots: trickle (never freeze at speed 0).
 * - Inside engageRange: full chase (if pack slot allows).
 * - Beyond engage: march (front slots) — still closing the map gap.
 */
export function swarmApproachKind(
  units: readonly IwUnit[],
  unit: IwUnit,
  targetX: number,
  targetY: number,
  knobs: CrowdKnobs = crowdKnobs(),
): SwarmApproachKind {
  const d = Math.hypot(targetX - unit.x, targetY - unit.y);
  if (!packAdvanceSlotOk(units, unit, targetX, targetY, knobs.packSplitRadius, knobs.packSplitMax)) {
    return "trickle";
  }
  if (d <= knobs.engageRange) return "chase";
  return "march";
}

export function approachSpeed(kind: SwarmApproachKind, baseSpeed: number): number {
  const knobs = crowdKnobs();
  if (kind === "trickle") return baseSpeed * knobs.trickleSpeedMul;
  if (kind === "chase") return baseSpeed;
  return baseSpeed * knobs.marchSpeedMul;
}

/** Apply approach toward a point; returns distance. Always steers unless speed is 0. */
export function applySwarmApproach(
  units: readonly IwUnit[],
  unit: IwUnit,
  targetX: number,
  targetY: number,
  baseSpeed: number,
): { kind: SwarmApproachKind; distance: number } {
  const kind = swarmApproachKind(units, unit, targetX, targetY);
  const distance = Math.hypot(targetX - unit.x, targetY - unit.y);
  const speed = approachSpeed(kind, baseSpeed);
  if (speed <= 0) {
    clearIntent(unit);
  } else {
    steerToward(unit, targetX, targetY, speed);
  }
  return { kind, distance };
}

export function applyCrowdSeparation(state: InfiniteWarState, step: number): void {
  const knobs = crowdKnobs();
  applySoftSepSpatial({
    items: state.units.filter((unit) => unit.phase === "alive"),
    hash: state.crowdHash,
    cellSize: U.crowd.cellSize,
    getId: (u) => u.id,
    getXY: (u) => ({ x: u.x, y: u.y }),
    queryRadius: (u) => roleStats(u.roleId).radius * 2.5,
    sepRadiusFor: (a, b) =>
      (roleStats(a.roleId).radius + roleStats(b.roleId).radius) * knobs.sepRadiusMul,
    sepStrength: U.crowd.sepStrength,
    sepTopK: U.crowd.sepTopK,
    sepMaxImpulse: U.crowd.sepMaxImpulse,
    walkEnter: U.crowd.walkEnter,
    getIntent: (u) => ({ intentVx: u.intentVx, intentVy: u.intentVy }),
    skipSubject: (u) => u.phase !== "alive",
    onIntegrate: (u, sx, sy) => {
      u.x += sx * step;
      u.y += sy * step;
    },
  });

  for (const unit of state.units) {
    unit.x = Math.max(38, Math.min(502, unit.x));
    unit.y = Math.max(156, Math.min(792, unit.y));
  }
}
